import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import canAuthenticateWithPassword from '../../account/canAuthenticateWithPassword';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Toast from '../../components/Toast';
import { gql } from '../../graphql/generated/gql';
import { PlanTier } from '../../graphql/generated/graphql';
import useSSOPlatforms from '../../integrations/useSSOPlatforms';
import useIsPasswordSet from '../../onboarding/useIsPasswordSet';
import BetaBadge from '../../organization/BetaBadge';
import UpgradeBadge from '../../organization/UpgradeBadge';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import { UIActions } from '../../ui/uiSlice';
import { isFeatureEnabled } from '../../utils/features';
import Link from '../../utils/router/Link';
import useUpdateEmailMutation from '../useUpdateEmailMutation';
import useUpdatePasswordMutation from '../useUpdatePasswordMutation';
import * as styles from './UserAccountSettings.module.scss';
import UserThemeSetting from './UserThemeSetting';
import useUpdateUserSettings from './useUpdateUserSettings';
import { openPricingModal } from '../../utils/url/pricingPath';

const ORGANIZATION_AUTHENTICATION_SETTING_QUERY = gql(/** @complexity 3 */ `
  query organizationAuthenticationSetting {
    session: currentSession {
      organization {
        id
        ...canAuthenticateWithPassword_organization
      }
    }
  }
`);

type EmailFormValues = {
  email?: string | null;
};

type UpdatePasswordFormValues = {
  oldPassword: string;
  password: string;
};

const UserAccountSettings = () => {
  const updateUserSettings = useUpdateUserSettings();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const {
    id,
    email,
    settings,
    type: userType,
  } = useAppSelector((state) => state.session.user);
  const { allowAccountAccess: shouldAllowAccountAccess = false } =
    settings ?? {};
  const { data: { session } = {} } = useQuery(
    ORGANIZATION_AUTHENTICATION_SETTING_QUERY,
  );
  const ssoPlatforms = useSSOPlatforms() || [];
  const { organization } = session ?? {};
  const userSettings = useAppSelector((state) => state.session.user?.settings);
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const emailChanged = searchParams.get('emailChanged');

    if (emailChanged) {
      const hasEmailChanged = emailChanged === '1';

      dispatch(
        UIActions.pushToast(
          ({ onDismiss }) => (
            <Toast
              level={hasEmailChanged ? 'success' : 'error'}
              onDismiss={onDismiss}
              title={hasEmailChanged ? 'Email updated' : 'Email update failed'}
              message={
                hasEmailChanged
                  ? 'Your email has been verified and updated successfully.'
                  : 'Oops, something went wrong while updating your email. Please try again later.'
              }
            />
          ),
          7000,
        ),
      );

      setSearchParams({}, { replace: true });
    }
  }, [dispatch, setSearchParams, searchParams]);

  const { data: isPasswordSet } = useIsPasswordSet();
  const updateUserEmail = useUpdateEmailMutation();
  const updateUserPassword = useUpdatePasswordMutation();
  const passwordText = isPasswordSet ? '********' : '';

  const handleUpdateEmail = (values: EmailFormValues) => {
    if (!values.email) return;

    updateUserEmail(id, values.email).then(() => {
      setIsEditingEmail(false);
      if (values.email !== email) {
        dispatch(
          UIActions.pushToast(
            ({ onDismiss }) => (
              <Toast
                level="warning"
                onDismiss={onDismiss}
                title="Verify your email"
                message="Please check your email inbox to complete the update to your new address."
              />
            ),
            7000,
          ),
        );
      }
    });
  };

  const handleUpdatePassword = (values: UpdatePasswordFormValues) =>
    updateUserPassword(values).then(() => setIsEditingPassword(false));
  const handleUpdateAccountAccess = (value: boolean) =>
    updateUserSettings({ allowAccountAccess: value });
  const handleUpdateAiAutofix = (value: boolean) =>
    updateUserSettings({ aiAutofix: value });
  const handleUpdateAiPredict = (value: boolean) =>
    updateUserSettings({ aiPredict: value });
  const handleUpdateAiAsk = (value: boolean) =>
    updateUserSettings({ aiAsk: value });
  const handleUpdatePreferDesktop = (value: boolean) =>
    updateUserSettings({ preferDesktop: value });

  return (
    <SettingsContainerView title="Account">
      <SettingsSection header="Authentication">
        <Cards>
          {isEditingEmail ? (
            <SettingCard
              description={
                <Form<EmailFormValues>
                  onSubmit={handleUpdateEmail}
                  initialValues={{ email }}
                  apiErrorMessages={{
                    DUPLICATE_EMAIL: 'Email already in use by another user',
                  }}
                >
                  {({ values }) => (
                    <div className={styles.fieldContainer}>
                      <Field
                        className={styles.field}
                        id="userEmail"
                        type="email"
                        name="email"
                        autoComplete="email"
                        margin="none"
                        autoFocus
                        required
                      />
                      <div>
                        <Submit
                          disabled={!values.email || values.email === email}
                          variant="primary"
                        >
                          Save
                        </Submit>
                      </div>
                    </div>
                  )}
                </Form>
              }
              title={<label htmlFor="userEmail">Email address</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingEmail(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Email address"
              description={email}
            />
          )}
          {organization &&
            canAuthenticateWithPassword(
              organization,
              ssoPlatforms,
              userType ?? undefined,
            ) &&
            (isEditingPassword ? (
              <SettingCard
                testId="passwordSettingCard"
                description={
                  <Form<UpdatePasswordFormValues>
                    onSubmit={handleUpdatePassword}
                    initialValues={{ oldPassword: '', password: '' }}
                    apiErrorMessages={{
                      FORBIDDEN: 'Current password is incorrect',
                    }}
                  >
                    {({ isValid, dirty }) => (
                      <>
                        <input
                          className={styles.hide}
                          name="email"
                          type="email"
                          value={email ?? undefined}
                          autoComplete="username"
                          readOnly
                          aria-label="Email address"
                        />
                        {isPasswordSet && (
                          <Field
                            type="password"
                            name="oldPassword"
                            label="Current password"
                            autoComplete="current-password"
                            margin="small"
                            autoFocus
                            required
                          />
                        )}
                        <Field
                          type="password"
                          name="password"
                          label="New password"
                          autoComplete="new-password"
                          helpText={`${
                            isPasswordSet
                              ? ''
                              : 'You’ve never picked a password. '
                          }
                              Choose a password at least 8 characters in length.`}
                          margin="small"
                          autoFocus={!isPasswordSet}
                          required
                        />
                        <div className={styles.submitContainer}>
                          <Submit
                            disabled={!isValid || !dirty}
                            variant="primary"
                          >
                            Save
                          </Submit>
                        </div>
                      </>
                    )}
                  </Form>
                }
              />
            ) : (
              <SettingCard
                testId="passwordSettingCard"
                actions={
                  <Button
                    onClick={() => setIsEditingPassword(true)}
                    variant="secondary"
                    disabled={isPasswordSet === undefined}
                  >
                    Edit
                  </Button>
                }
                title="Password"
                description={
                  isPasswordSet === undefined ? (
                    <LoadingIndicator />
                  ) : (
                    passwordText
                  )
                }
              />
            ))}
        </Cards>
      </SettingsSection>
      <SettingsSection header="Personalization">
        <Cards>
          {isFeatureEnabled('ai_ask') && (
            <SettingCard
              title={
                <>
                  AI Ask
                  <span className={styles.badge}>
                    <BetaBadge />
                  </span>
                </>
              }
              description="Get answers using accessible published posts"
              actions={
                <Form initialValues={{ ask: !!userSettings?.aiAsk }}>
                  <Field
                    name="ask"
                    onChange={(e: React.ChangeEvent) => {
                      if ('checked' in e.target) {
                        handleUpdateAiAsk(Boolean(e.target.checked));
                      }
                    }}
                    toggle
                    type="checkbox"
                  />
                </Form>
              }
            />
          )}
          <SettingCard
            title="AI Autofix"
            description="Automatically catch and fix typos when editing posts"
            actions={
              <Form initialValues={{ autofix: !!userSettings?.aiAutofix }}>
                <Field
                  name="autofix"
                  onChange={(e: React.ChangeEvent) => {
                    if ('checked' in e.target) {
                      handleUpdateAiAutofix(Boolean(e.target.checked));
                    }
                  }}
                  toggle
                  type="checkbox"
                />
              </Form>
            }
          />
          <SettingCard
            title={
              <>
                AI Predict
                {planTier === PlanTier.Free && (
                  <span className={styles.badge}>
                    <UpgradeBadge
                      onClick={() => {
                        openPricingModal(dispatch, 'ai_predict');
                      }}
                    />
                  </span>
                )}
              </>
            }
            description="Predict and suggest your next words when editing posts"
            actions={
              <Form
                initialValues={{
                  predict:
                    isFeatureEnabled('ai_predict') && !!userSettings?.aiPredict,
                }}
              >
                <Field
                  name="predict"
                  disabled={!isFeatureEnabled('ai_predict')}
                  onChange={(e: React.ChangeEvent) => {
                    if ('checked' in e.target) {
                      handleUpdateAiPredict(Boolean(e.target.checked));
                    }
                  }}
                  toggle
                  type="checkbox"
                />
              </Form>
            }
          />
          <UserThemeSetting />
          <SettingCard
            title="Open in desktop app"
            description={
              <>
                Open post links in the desktop app.{' '}
                <Link to="https://slab.com/downloads" newTab>
                  Download app
                </Link>
              </>
            }
            actions={
              <Form
                initialValues={{
                  preferDesktop: !!userSettings?.preferDesktop,
                }}
              >
                <Field
                  name="preferDesktop"
                  onChange={(e: React.ChangeEvent) => {
                    if ('checked' in e.target) {
                      handleUpdatePreferDesktop(Boolean(e.target.checked));
                    }
                  }}
                  toggle
                  type="checkbox"
                />
              </Form>
            }
          />
        </Cards>
      </SettingsSection>
      <SettingsSection header="Support">
        <Cards>
          <SettingCard
            title="Give Slab access"
            description="Allow Slab’s support staff to access your account to diagnose and resolve issues you’ve raised."
            actions={
              <Form
                initialValues={{
                  allowAccountAccess: shouldAllowAccountAccess ?? false,
                }}
              >
                <Field
                  name="allowAccountAccess"
                  onChange={(e: React.ChangeEvent) => {
                    if ('checked' in e.target) {
                      handleUpdateAccountAccess(Boolean(e.target.checked));
                    }
                  }}
                  toggle
                  type="checkbox"
                />
              </Form>
            }
          />
        </Cards>
      </SettingsSection>
    </SettingsContainerView>
  );
};

export default UserAccountSettings;
